
import { Link } from 'react-router-dom';
import { WishlistButton } from './WishlistButton';

interface PropertyCardProps {
  id: string;
  title: string;
  location: string;
  address: string;
  price: number;
  currency: string;
  bedrooms: number;
  bathrooms: number;
  parking: number;
  area: number;
  image: string;
  ribbon?: string;
  listingType?: string;
  priceUnit?: string;
  maxGuests?: number;
}

export const PropertyCard = ({
  id,
  title,
  location,
  address,
  price,
  currency,
  bedrooms,
  bathrooms,
  parking,
  area,
  image,
  ribbon,
  listingType,
  priceUnit,
  maxGuests,
}: PropertyCardProps) => {
  const isShortTerm = listingType === 'short-term';

  const getDetailLink = () => {
    if (isShortTerm) {
      return `/short-term/${id}`;
    }
    return `/property/${id}`;
  };

  const getPriceLabel = () => {
    if (isShortTerm) {
      if (priceUnit === 'week') return '/week';
      if (priceUnit === 'month') return '/month';
      return '/night';
    }
    return '';
  };

  const getRibbonStyle = () => {
    if (ribbon === 'AIRBNB' || ribbon === 'SHORT STAY') {
      return 'bg-gradient-to-r from-amber-500 to-orange-500';
    }
    if (ribbon === 'WEEKLY') {
      return 'bg-gradient-to-r from-teal-500 to-emerald-500';
    }
    return 'bg-gray-800';
  };

  // Format price with proper abbreviation for large numbers
  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `${(price / 1000000).toFixed(1)}M`;
    }
    if (price >= 1000) {
      return `${(price / 1000).toFixed(0)}K`;
    }
    return price.toLocaleString();
  };

  return (
    <Link to={getDetailLink()} className="group cursor-pointer block">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden card-hover">
        {/* Image Container */}
        <div className="relative aspect-[4/3] sm:aspect-[16/11] overflow-hidden">
          {ribbon && (
            <div
              className={`absolute top-2 sm:top-4 left-0 ${getRibbonStyle()} text-white px-2 sm:px-6 py-1 sm:py-2 text-[10px] sm:text-sm font-bold tracking-wider z-10 animate-fade-in-left flex items-center gap-1`}
            >
              {(ribbon === 'AIRBNB' || ribbon === 'SHORT STAY') && (
                <i className="ri-home-wifi-line text-xs sm:text-base"></i>
              )}
              {ribbon === 'WEEKLY' && (
                <i className="ri-calendar-check-line text-xs sm:text-base"></i>
              )}
              <span className="hidden xs:inline whitespace-nowrap">{ribbon}</span>
            </div>
          )}

          {/* Wishlist Button */}
          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-20">
            <WishlistButton propertyId={id} size="md" />
          </div>

          {/* Image */}
          <div className="w-full h-full img-zoom">
            <img 
              src={image} 
              alt={title} 
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          {/* Short-term badge */}
          {isShortTerm && (
            <div className="absolute bottom-12 sm:bottom-16 right-2 sm:right-4 bg-white/90 backdrop-blur-sm px-1.5 sm:px-3 py-0.5 sm:py-1.5 rounded-full flex items-center gap-0.5 sm:gap-1.5 shadow-lg">
              <i className="ri-home-wifi-line text-amber-500 text-xs sm:text-sm"></i>
              <span className="text-[10px] sm:text-xs font-semibold text-gray-700 whitespace-nowrap">Short Stay</span>
            </div>
          )}

          {/* Quick action buttons on hover - hidden on mobile */}
          <div className="hidden sm:block absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
            <button className="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full hover:bg-white hover:scale-110 transition-all duration-300 cursor-pointer">
              <i className="ri-share-line text-gray-700"></i>
            </button>
          </div>

          {/* Price Tag - Improved mobile responsiveness */}
          <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 bg-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg shadow-lg transform group-hover:scale-105 transition-transform duration-300 max-w-[45%] sm:max-w-none">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-1">
              <span className="text-coral-500 text-sm sm:text-xl font-bold whitespace-nowrap truncate">{currency}{formatPrice(price)}</span>
              <span className="text-gray-500 text-[10px] sm:text-sm whitespace-nowrap">{getPriceLabel()}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-5">
          {/* Property Stats */}
          <div className="flex items-center justify-between mb-2 sm:mb-4 text-gray-600 gap-1">
            {/* Bedrooms */}
            <div className="flex items-center gap-0.5 sm:gap-1 justify-center min-w-0">
              <i className="ri-hotel-bed-line text-sm sm:text-lg group-hover:text-coral-500 transition-colors duration-300 flex-shrink-0"></i>
              <span className="text-xs sm:text-sm font-medium">{bedrooms}</span>
            </div>

            {/* Bathrooms */}
            <div className="flex items-center gap-0.5 sm:gap-1 justify-center min-w-0">
              <i className="ri-shower-line text-sm sm:text-lg group-hover:text-coral-500 transition-colors duration-300 flex-shrink-0"></i>
              <span className="text-xs sm:text-sm font-medium">{bathrooms}</span>
            </div>

            {/* Guests or Parking */}
            {isShortTerm && maxGuests ? (
              <div className="flex items-center gap-0.5 sm:gap-1 justify-center min-w-0">
                <i className="ri-user-line text-sm sm:text-lg group-hover:text-coral-500 transition-colors duration-300 flex-shrink-0"></i>
                <span className="text-xs sm:text-sm font-medium">{maxGuests}</span>
              </div>
            ) : (
              <div className="flex items-center gap-0.5 sm:gap-1 justify-center min-w-0">
                <i className="ri-car-line text-sm sm:text-lg group-hover:text-coral-500 transition-colors duration-300 flex-shrink-0"></i>
                <span className="text-xs sm:text-sm font-medium">{parking}</span>
              </div>
            )}

            {/* Area */}
            <div className="flex items-center gap-0.5 sm:gap-1 justify-center min-w-0">
              <i className="ri-home-4-line text-sm sm:text-lg group-hover:text-coral-500 transition-colors duration-300 flex-shrink-0"></i>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{area}</span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-sm sm:text-lg font-bold text-gray-800 mb-1 group-hover:text-coral-600 transition-colors duration-300 line-clamp-1">
            {title}
          </h3>
          
          {/* Location */}
          <div className="flex items-start gap-1 text-gray-600 min-w-0">
            <i className="ri-map-pin-line text-coral-500 text-xs sm:text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-xs sm:text-sm truncate min-w-0">{location}</p>
          </div>
          <p className="text-[10px] sm:text-sm text-gray-500 mt-0.5 sm:mt-1 truncate">{address}</p>
        </div>
      </div>
    </Link>
  );
};
